import { useQuery } from '@apollo/client';
import { AiChatChannel } from '@shared/realtime';
import { useCallback, useEffect } from 'react';
import { useGetRealtimeToken } from 'src/modules/entities/realtime';
import { useInngestRealtime } from '../../../hooks/useInngestRealtime';
import AiChatApi from '../api/AiChat.api';
import { useAiChatStore } from '../store/aiChat.store';

export default function useAgent({ onReply }: { onReply?: () => void }) {
  const getConversationId = useAiChatStore((state) => state.getConversationId);
  const appendStreamingMessage = useAiChatStore((state) => state.appendStreamingMessage);
  const finalizeStreamingMessage = useAiChatStore((state) => state.finalizeStreamingMessage);
  const upsertStatus = useAiChatStore((state) => state.upsertStatus);
  const completeStatus = useAiChatStore((state) => state.completeStatus);
  const setRealtimeState = useAiChatStore((state) => state.setRealtimeState);
  const updateConversationTitle = useAiChatStore((state) => state.updateConversationTitle);
  const enabled = !!getConversationId();
  const { getRealtimeToken } = useGetRealtimeToken();
  const { refetch: getMessage } = useQuery(AiChatApi.GetMessage, { skip: true });

  const getSubscriptionToken = useCallback(async () => {
    const conversationId = getConversationId();

    if (!conversationId) {
      // Was `return null`: inngest/react's resolveToken threw a TypeError on it, the same catch as this throw.
      throw new Error('No conversation id for realtime subscription');
    }

    const { data } = await getRealtimeToken({
      variables: {
        channel: AiChatChannel.getChannelId({ conversationId }),
        topics: [
          AiChatChannel.topics.message,
          AiChatChannel.topics.messageStream,
          AiChatChannel.topics.toolCall,
          AiChatChannel.topics.toolResult,
          AiChatChannel.topics.thinking,
          AiChatChannel.topics.status,
          AiChatChannel.topics.titleUpdate,
        ],
      },
    });

    const response = data?.getRealtimeToken;
    if (!response) {
      throw new Error('Failed to fetch chat subscription token');
    }

    return response;
  }, [getRealtimeToken, getConversationId]);

  const realtime = useInngestRealtime<typeof AiChatChannel>({
    enabled,
    getSubscriptionToken,
    onStateChange: setRealtimeState,
  });

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    const unsubscribeMessages = realtime.subscribe(AiChatChannel.topics.message, async ({ data }) => {
      // If messageId is present, fetch full message with documents
      if (data.messageId) {
        try {
          const result = await getMessage({ id: data.messageId });
          if (result.data?.message) {
            // Finalize streaming message atomically (no flash)
            finalizeStreamingMessage(result.data.message);
          } else {
            // Fallback to data from channel if query fails
            finalizeStreamingMessage(data);
          }
        } catch (error) {
          console.error('Failed to fetch full message:', error);
          // Fallback to data from channel
          finalizeStreamingMessage(data);
        }
      } else {
        // No messageId, use data from channel
        finalizeStreamingMessage(data);
      }

      onReply?.();
    });

    const unsubscribeMessageStream = realtime.subscribe(AiChatChannel.topics.messageStream, ({ data }) => {
      if (data.type === 'delta' && data.delta) {
        appendStreamingMessage(data.delta);
      } else if (data.type === 'finish') {
        // Content block finished - add newline to separate from next block
        appendStreamingMessage('\n\n');
      }
    });

    // Legacy thinking handler - kept for backwards compatibility
    // New code should use the 'status' topic with groupId='thinking'
    const unsubscribeThinking = realtime.subscribe(AiChatChannel.topics.thinking, ({ data }) => {
      upsertStatus('thinking-legacy', { status: 'pending', message: data.message });
    });

    // New unified status handler - handles start/complete for all status groups
    const unsubscribeStatus = realtime.subscribe(AiChatChannel.topics.status, ({ data }) => {
      if (data.type === 'start') {
        upsertStatus(data.groupId, { status: 'pending', message: data.message || 'Working...', label: data.label });
      } else if (data.type === 'complete') {
        completeStatus(data.groupId);
      }
    });

    // Tool call updates the agent's status message (but doesn't complete it)
    const unsubscribeToolCall = realtime.subscribe(AiChatChannel.topics.toolCall, ({ data }) => {
      upsertStatus(`agent-${data.agentId}`, { status: 'pending', message: data.message, label: data.agentTask });
    });

    const unsubscribeToolResult = realtime.subscribe(AiChatChannel.topics.toolResult, () => {
      // No-op: Agent completion is now handled by status topic with type='complete'
    });

    // Title update handler - updates the conversation title in real-time
    const unsubscribeTitleUpdate = realtime.subscribe(AiChatChannel.topics.titleUpdate, ({ data }) => {
      updateConversationTitle(data.title);
    });

    return () => {
      unsubscribeMessages();
      unsubscribeMessageStream();
      unsubscribeThinking();
      unsubscribeStatus();
      unsubscribeToolCall();
      unsubscribeToolResult();
      unsubscribeTitleUpdate();
    };
  }, [
    enabled,
    realtime,
    appendStreamingMessage,
    finalizeStreamingMessage,
    upsertStatus,
    completeStatus,
    getMessage,
    onReply,
    updateConversationTitle,
  ]);
}
